import type { ReactNode } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Pencil component `LOrAQ` (`v2/StatCard`). Compact white card with title +
 * big number + (optional) delta chip + (optional) sparkline.
 *
 * Layout note: card stretches via `flex flex-col h-full` so when several
 * StatCards are placed in a CSS grid row, they all share the row's height.
 * The bottom block (delta + sparkline) is anchored with `mt-auto` so cards
 * with no footer content still match the height of cards that have it.
 */
export function StatCard({
  label,
  value,
  deltaPct,
  delta,
  hint,
  trend,
  className,
}: {
  label: string;
  value: ReactNode;
  /**
   * Legacy: percentage delta as a whole-number percent (e.g. 12 = +12%).
   * Positive renders green ↑, negative renders red ↓.
   * Prefer the richer `delta` prop for new code.
   */
  deltaPct?: number | null;
  /**
   * Period-over-period delta. `value` is a fractional ratio (0.12 = +12%)
   * unless `suffix` is given. `invertGoodBad` flips colors so a *down*
   * arrow renders green — useful for cancellation / no-show / churn.
   */
  delta?: {
    value: number;
    /** "%" by default; "pp" for percentage-point comparisons. */
    suffix?: string;
    label?: string;
    invertGoodBad?: boolean;
  };
  hint?: string;
  /** Optional sparkline strip below the value (~24px tall). */
  trend?: number[];
  className?: string;
}) {
  return (
    <div className={cn("rounded-xl border bg-card p-5 flex flex-col h-full", className)}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="mt-2 flex items-baseline gap-3">
        <p className="text-3xl font-semibold tracking-tight">{value}</p>
        {typeof deltaPct === "number" && !delta && (
          <span
            className={cn(
              "inline-flex items-center gap-0.5 text-xs font-medium",
              deltaPct >= 0 ? "text-trend-positive" : "text-trend-negative",
            )}
          >
            {deltaPct >= 0 ? (
              <ArrowUp className="h-3 w-3" />
            ) : (
              <ArrowDown className="h-3 w-3" />
            )}
            {Math.abs(deltaPct).toFixed(0)}%
          </span>
        )}
      </div>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}

      {(delta || trend) && (
        <div className="mt-auto pt-3 flex items-end justify-between gap-3">
          {delta ? (
            <DeltaChip
              value={delta.value}
              suffix={delta.suffix ?? "%"}
              label={delta.label ?? "vs prior period"}
              invertGoodBad={delta.invertGoodBad}
            />
          ) : (
            <span />
          )}
          {trend && trend.length > 1 && <Sparkline data={trend} />}
        </div>
      )}
    </div>
  );
}

function DeltaChip({
  value,
  suffix,
  label,
  invertGoodBad,
}: {
  value: number;
  suffix: string;
  label: string;
  invertGoodBad?: boolean;
}) {
  const isUp = value >= 0;
  const isGood = invertGoodBad ? !isUp : isUp;
  const tone = value === 0
    ? "text-muted-foreground"
    : isGood
      ? "text-trend-positive"
      : "text-trend-negative";
  // Render "%" as a fraction times 100 for readability when suffix is "%".
  const display = suffix === "%"
    ? `${Math.abs(value * 100).toFixed(0)}%`
    : `${Math.abs(value).toFixed(value < 1 ? 1 : 0)}${suffix}`;
  return (
    <div className="inline-flex flex-col">
      <span className={cn("inline-flex items-center gap-0.5 text-xs font-medium", tone)}>
        {value !== 0 && (isUp ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
        {display}
      </span>
      <span className="text-[10px] text-muted-foreground/80 leading-tight">
        {label}
      </span>
    </div>
  );
}

/**
 * Tiny inline SVG sparkline. Polyline scaled to the data range. Renders at
 * a fixed height (24px) and 80px wide — fits beside the delta chip without
 * fighting it for space.
 */
function Sparkline({ data }: { data: number[] }) {
  const w = 80;
  const h = 24;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const stepX = w / (data.length - 1);
  const points = data
    .map((v, i) => {
      const x = i * stepX;
      const y = h - ((v - min) / span) * (h - 2) - 1;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      className="text-primary/70 shrink-0"
      aria-hidden
    >
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
